import { useTheme } from "next-themes";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Brand-aligned Sonner toaster.
 *
 * Visual goals:
 *   - Bottom-right placement, comfortable spacing from the viewport edge.
 *   - Glassy, branded surface that matches the app (rounded, soft shadow,
 *     subtle border, blurred background).
 *   - Clear, color-coded variants for success / error / warning / info via
 *     a left accent stripe — never the loud full-bleed defaults.
 *   - Tight typography (Inter body, sentence-case titles).
 */
const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      position="bottom-right"
      offset={20}
      gap={10}
      duration={4500}
      visibleToasts={4}
      closeButton
      className="toaster group"
      toastOptions={{
        unstyled: false,
        classNames: {
          toast:
            [
              "group toast pointer-events-auto relative overflow-hidden",
              "flex items-start gap-3 p-4 pl-5 pr-10",
              "rounded-2xl border border-border/70",
              "bg-background/95 supports-[backdrop-filter]:bg-background/80 supports-[backdrop-filter]:backdrop-blur-md",
              "text-foreground shadow-[0_10px_30px_-12px_hsl(var(--foreground)/0.18)]",
              "transition-all duration-200",
              // Left accent stripe — colored per variant via the variant rules below.
              "before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-full before:bg-primary",
            ].join(" "),
          title: "text-sm font-medium leading-snug",
          description: "text-sm text-muted-foreground leading-snug mt-0.5",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-lg group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs group-[.toast]:font-medium",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-lg group-[.toast]:px-3 group-[.toast]:py-1.5 group-[.toast]:text-xs",
          closeButton:
            "!bg-transparent !border-0 !text-muted-foreground hover:!text-foreground !left-auto !right-2 !top-2",
          icon: "shrink-0 mt-0.5 [&_svg]:w-4 [&_svg]:h-4",
          // Variant-specific accent colors (left stripe + icon tint).
          success:
            "before:!bg-emerald-500 [&_[data-icon]_svg]:text-emerald-500",
          error:
            "before:!bg-destructive [&_[data-icon]_svg]:text-destructive",
          warning:
            "before:!bg-amber-500 [&_[data-icon]_svg]:text-amber-500",
          info:
            "before:!bg-primary [&_[data-icon]_svg]:text-primary",
        },
      }}
      {...props}
    />
  );
};

export { Toaster, toast };
